import { useState } from 'react';

import { useGetContext } from '@api/lendingAPI/lapiHooks';
import { IconShieldCheck, IconZoomOutline } from '@iwoca/icons';
import { Input, Spinner } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { FormattedMessage, useIntl } from 'react-intl';

import { TOnlineBankWithProvider } from './BankSelectionModal.types';
import { getBanksFromContext } from './getBanksFromContext';
import FallbackErrorModal from '../fallbackErrorModal/FallbackErrorModal';

export const ChooseBank = ({
    selectBank
}: {
    selectBank: (bank: TOnlineBankWithProvider) => void;
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const intl = useIntl();

    return (
        <div>
            <div className="space-y-l">
                <div className="flex flex-row justify-between">
                    <Text className="font-bold">
                        <FormattedMessage
                            id="_.bank_selection_modal.how_does_it_work"
                            defaultMessage="How does it work?"
                        />
                    </Text>
                    <Text className="inline-flex items-center gap-s text-s font-med">
                        <IconShieldCheck />
                        <FormattedMessage
                            id="_.bank_selection_modal.bank_grade_encryption"
                            defaultMessage="Bank-grade encryption"
                        />
                    </Text>
                </div>

                <Text>
                    <FormattedMessage
                        id="_.bank_selection_modal.description"
                        defaultMessage="Securely share your bank account’s data. Simply choose your bank below and then log in through your bank’s portal when prompted."
                    />
                </Text>
            </div>
            <div className="mt-xl space-y-l">
                <Input
                    iconLeft={<IconZoomOutline />}
                    type="text"
                    placeholder={intl.formatMessage({
                        id: '_.bank_selection_modal.search_placeholder',
                        defaultMessage: 'Search for your bank...'
                    })}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <ListOfBanks
                    searchQuery={searchQuery}
                    selectBank={selectBank}
                />
            </div>
        </div>
    );
};

const ListOfBanks = ({
    searchQuery,
    selectBank
}: {
    searchQuery: string;
    selectBank: (bank: TOnlineBankWithProvider) => void;
}) => {
    const { data: context, isLoading, isError } = useGetContext();

    if (isLoading) {
        <div className="flex justify-center pt-xl">
            <Spinner size="large" />
        </div>;
    }

    if (isError) return <FallbackErrorModal />;

    const banks = getBanksFromContext(context);
    const filteredBanks = banks.filter((bank) =>
        bank.bank_name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (!filteredBanks || filteredBanks.length === 0)
        return (
            <div>
                <Text>
                    <FormattedMessage
                        id="_.bank_selection_modal.no_banks_available"
                        defaultMessage="Unfortunately the bank you have chosen is not supported."
                    />
                </Text>
            </div>
        );

    return (
        <div className="grid grid-cols-4 gap-m">
            {filteredBanks.map((bank) => (
                <button
                    key={bank.bank_id}
                    className="flex items-center rounded-s border border-structure-95 p-s"
                    onClick={() => selectBank(bank)}
                >
                    <figure className="mx-auto text-center">
                        <img
                            data-testid={`bank-image-${bank.bank_name}`}
                            src={bank.logo_url}
                            alt={bank.bank_name}
                            className="fs-mask dd-privacy-mask mx-auto max-w-[40px]"
                        />
                        <figcaption className="mt-s text-xs">
                            {bank.bank_name}
                        </figcaption>
                    </figure>
                </button>
            ))}
        </div>
    );
};
